// ============================================
// Path: /frontend/src/pages/MyListingsPage.jsx
// UPDATED: Added delete-for-new-listing flow
// CSS OPTIMIZED: Mobile-first responsive design
// ============================================

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth.jsx';
import { useToast } from '../hooks/useToast';
import { listingsAPI } from '../api/listings.api';
import api from '../api/client';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Eye, Loader2, Trash2, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';

// ========================================
// LOADING SKELETON COMPONENT
// ========================================
const LoadingSkeleton = () => (
  <CardContent className="p-3 sm:p-4 md:p-6">
    {/* CSS FIX 1: Mobile - hide table, show card layout */}
    <div className="block md:hidden space-y-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="border rounded-lg p-3 space-y-2">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-4 w-1/2" />
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      ))}
    </div>
    {/* CSS FIX 2: Desktop - show table */}
    <div className="hidden md:block overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-xs sm:text-sm">Title</TableHead>
            <TableHead className="text-xs sm:text-sm">Category</TableHead>
            <TableHead className="text-xs sm:text-sm">Status</TableHead>
            <TableHead className="text-xs sm:text-sm">Created On</TableHead>
            <TableHead className="text-xs sm:text-sm">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 3 }).map((_, i) => (
            <TableRow key={i}>
              <TableCell>
                <Skeleton className="h-4 sm:h-5 w-36 sm:w-48" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 sm:h-5 w-20 sm:w-24" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-5 sm:h-6 w-16 sm:w-20 rounded-full" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-4 sm:h-5 w-24 sm:w-32" />
              </TableCell>
              <TableCell>
                <Skeleton className="h-7 sm:h-8 w-7 sm:w-8 rounded-md" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </CardContent>
);

// ========================================
// MAIN COMPONENT
// ========================================
export default function MyListingsPage() {
  console.log('🎨 [MyListingsPage] Component mounted');

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ⭐ NEW: Check if user came here to delete for new listing
  const isDeletingForNew = sessionStorage.getItem('deletingForNewListing') === 'true';
  const deleteMessage = location.state?.message;

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    const fetchUserListings = async () => {
      if (!user) {
        console.log('⚠️ [MyListingsPage] No user, redirecting...');
        navigate('/login?redirect=/my-listings');
        return;
      }

      console.log('🔵 [MyListingsPage] Fetching user listings...', {
        userId: user.id,
      });

      setLoading(true);

      try {
        const data = await listingsAPI.getUserListings(user.id);
        console.log('✅ [MyListingsPage] Listings received:', data);
        setListings(data.listings || []);
        setError(null);
      } catch (err) {
        console.error('❌ [MyListingsPage] Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load your listings');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchUserListings();
    }
  }, [user, authLoading, navigate]);

  // ========================================
  // COMPUTED VALUES
  // ========================================
  const sortedListings = useMemo(() => {
    if (!listings) return [];
    console.log('🔄 [MyListingsPage] Sorting listings...');
    return [...listings].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
    );
  }, [listings]);

  // ========================================
  // HELPER FUNCTIONS
  // ========================================
  const getStatusVariant = (status) => {
    switch (status) {
      case 'APPROVED':
        return 'default';
      case 'PENDING':
        return 'secondary';
      case 'REJECTED':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // ⭐ NEW: Handle delete listing
  const handleDelete = async (listingId) => {
    if (!confirm('Are you sure you want to delete this listing?')) {
      return;
    }

    try {
      setDeletingId(listingId);
      console.log('[MyListingsPage] Deleting listing:', listingId);

      const response = await api.delete(`/listings/${listingId}`);

      if (response.data.success) {
        toast({
          title: 'Success',
          description: 'Listing deleted successfully.',
        });

        // Remove from local state
        setListings(listings.filter(l => l.id !== listingId));

        // ⭐ If deleting to make room for new listing, redirect back
        if (isDeletingForNew) {
          console.log('[MyListingsPage] Redirecting back to create listing...');
          
          toast({
            title: 'Space Created',
            description: 'You can now complete your new listing.',
          });

          // Wait a moment then redirect
          setTimeout(() => {
            navigate('/listings/create');
          }, 1500);
        }
      }
    } catch (error) {
      console.error('[MyListingsPage] Delete error:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete listing.',
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ========================================
  // LOADING STATE
  // ========================================
  if (authLoading) {
    console.log('⏳ [MyListingsPage] Loading auth...');
    return (
      <div className="flex flex-col min-h-screen">
        <Header />
        <main className="flex-grow flex items-center justify-center">
          {/* CSS FIX 3: Loading spinner sizing */}
          <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!user) {
    console.log('⚠️ [MyListingsPage] No user found');
    return null;
  }

  // ========================================
  // RENDER
  // ========================================
  console.log('🎨 [MyListingsPage] Rendering with listings:', {
    count: sortedListings.length,
    loading,
    isDeletingForNew,
  });

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      {/* CSS FIX 4: Main container padding - py-12 → py-6 sm:py-8 md:py-12 + px-4 */}
      <main className="flex-grow container py-6 sm:py-8 md:py-12 px-4">
        <Card>
          {/* CSS FIX 5: CardHeader padding */}
          <CardHeader className="p-4 sm:p-5 md:p-6">
            {/* CSS FIX 6: CardTitle sizing - text-3xl → text-xl sm:text-2xl md:text-3xl */}
            <CardTitle className="text-xl sm:text-2xl md:text-3xl">My Listings</CardTitle>
            {/* CSS FIX 7: CardDescription sizing */}
            <CardDescription className="text-xs sm:text-sm md:text-base">
              Here you can track the status of all your submitted listings.
            </CardDescription>
          </CardHeader>

          {/* ⭐ NEW: Delete-for-new alert */}
          {isDeletingForNew && (
            // CSS FIX 8: Alert container padding - pb-0 → p-3 sm:p-4 md:p-6 pb-0
            <CardContent className="p-3 sm:p-4 md:p-6 pb-0">
              {/* CSS FIX 9: Alert padding */}
              <Alert className="border-orange-500 bg-orange-50 p-3 sm:p-4">
                {/* CSS FIX 10: Alert icon sizing - h-4 w-4 → h-3.5 w-3.5 sm:h-4 sm:w-4 */}
                <AlertCircle className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-700" />
                {/* CSS FIX 11: Alert title sizing */}
                <AlertTitle className="text-orange-800 text-sm sm:text-base">Delete a Listing</AlertTitle>
                {/* CSS FIX 12: Alert description sizing */}
                <AlertDescription className="text-orange-700 text-xs sm:text-sm">
                  {deleteMessage || 'Select a listing to delete and make room for your new ad.'}
                </AlertDescription>
              </Alert>
            </CardContent>
          )}

          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            // CSS FIX 13: Error message padding and sizing
            <CardContent className="p-4 sm:p-5 md:p-6">
              <p className="text-destructive text-sm sm:text-base">
                Error loading your listings: {error}
              </p>
            </CardContent>
          ) : (
            <CardContent className="p-3 sm:p-4 md:p-6">
              {/* CSS FIX 14: Mobile card view */}
              <div className="block md:hidden space-y-3">
                {sortedListings.map((listing) => (
                  <div key={listing.id} className="border rounded-lg p-3 space-y-2">
                    {/* Listing Title */}
                    <div className="font-medium text-sm sm:text-base">
                      {listing.title}
                    </div>
                    {/* Category and Status */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-muted-foreground capitalize">
                        {listing.category?.toLowerCase()}
                      </span>
                      <Badge
                        variant={getStatusVariant(listing.status || 'PENDING')}
                        className="capitalize text-xs"
                      >
                        {listing.status?.toLowerCase() || 'pending'}
                      </Badge>
                    </div>
                    {/* Date */}
                    <div className="text-xs text-muted-foreground">
                      {listing.createdAt
                        ? format(new Date(listing.createdAt), 'PPP')
                        : 'N/A'}
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2">
                      <Link to={`/listing/${listing.id}`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full text-xs h-8">
                          <Eye className="h-3.5 w-3.5 mr-1.5" />
                          View
                        </Button>
                      </Link>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-8 px-3"
                        onClick={() => handleDelete(listing.id)}
                        disabled={deletingId === listing.id}
                      >
                        {deletingId === listing.id ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* CSS FIX 15: Desktop table view with horizontal scroll */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {/* CSS FIX 16: Table header text sizing */}
                      <TableHead className="text-xs lg:text-sm">Listing Title</TableHead>
                      <TableHead className="text-xs lg:text-sm">Category</TableHead>
                      <TableHead className="text-xs lg:text-sm">Status</TableHead>
                      <TableHead className="text-xs lg:text-sm">Date Submitted</TableHead>
                      <TableHead className="text-center text-xs lg:text-sm">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedListings.map((listing) => (
                      <TableRow key={listing.id}>
                        {/* CSS FIX 17: Table cell text sizing */}
                        <TableCell className="font-medium text-xs sm:text-sm">
                          {listing.title}
                        </TableCell>
                        <TableCell className="capitalize text-xs sm:text-sm">
                          {listing.category?.toLowerCase()}
                        </TableCell>
                        <TableCell>
                          {/* CSS FIX 18: Badge sizing */}
                          <Badge
                            variant={getStatusVariant(listing.status || 'PENDING')}
                            className="capitalize text-xs"
                          >
                            {listing.status?.toLowerCase() || 'pending'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">
                          {listing.createdAt
                            ? format(new Date(listing.createdAt), 'PPP')
                            : 'N/A'}
                        </TableCell>
                        <TableCell className="text-center">
                          {/* CSS FIX 19: Action buttons gap */}
                          <div className="flex items-center justify-center gap-1 sm:gap-2">
                            <Link to={`/listing/${listing.id}`}>
                              <Button variant="ghost" size="icon" title="View Listing" className="h-8 w-8 sm:h-9 sm:w-9">
                                {/* CSS FIX 20: Icon sizing */}
                                <Eye className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                              </Button>
                            </Link>
                            {/* ⭐ NEW: Delete button */}
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Delete Listing"
                              className="h-8 w-8 sm:h-9 sm:w-9"
                              onClick={() => handleDelete(listing.id)}
                              disabled={deletingId === listing.id}
                            >
                              {deletingId === listing.id ? (
                                <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-destructive" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* CSS FIX 21: Empty state */}
              {sortedListings.length === 0 && !loading && (
                <div className="text-center py-12 sm:py-16 px-4">
                  {/* CSS FIX 22: Empty state text sizing */}
                  <p className="text-muted-foreground mb-3 sm:mb-4 text-sm sm:text-base">
                    Your listing will show when approved by admin.
                  </p>
                  {/* CSS FIX 23: Empty state button sizing */}
                  <Button asChild className="text-xs sm:text-sm h-9 sm:h-10">
                    <Link to="/listings/create">Create Another Listing</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </main>
      <Footer />
    </div>
  );
}